// @file Composable that provides actions for whiteboard frames (switch, add, delete, move, etc.)
import { setSearchParam } from '@@/bits/location'
import { FRAME_ID_QUERY_PARAM } from '@@/bits/whiteboard'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardFrameBackgroundStore } from '@@/pinia/whiteboard_frame_background'
import { useWhiteboardModeStore } from '@@/pinia/whiteboard_mode'
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import { prefetchImagesInAdjacentFrames } from '@@/vuecomposables/use_whiteboard_play_mode_prefetch_images'
import type { IndexKey, TLPage, TLPageId } from 'tldraw'
import { getIndexAbove, getIndexBelow, getIndexBetween, PageRecordType } from 'tldraw'

/**
 * Determine the new index key based on surrounding pages.
 */
const getNewIndexKey = (pageBefore: TLPage | null, pageAfter: TLPage | null): IndexKey => {
  if (pageBefore != null && pageAfter != null) {
    return getIndexBetween(pageBefore.index, pageAfter.index)
  }
  if (pageBefore != null) {
    return getIndexAbove(pageBefore.index)
  }
  if (pageAfter != null) {
    return getIndexBelow(pageAfter.index)
  }
  throw new Error('Cannot determine index key without at least one reference page')
}

/**
 * Calculate a new index key for moving an existing frame from one position to another.
 */
const calculateIndexKeyForMoveFrame = (fromIndex: number, toIndex: number): IndexKey | null => {
  const editor = useTldrawEditor().editor
  const pages = editor?.getPages()
  if (pages == null) {
    return null
  }
  const isMovingToLowerIndex = fromIndex > toIndex
  const pageBefore = isMovingToLowerIndex ? pages[toIndex - 1] : pages[toIndex]
  const pageAfter = isMovingToLowerIndex ? pages[toIndex] : pages[toIndex + 1]
  return getNewIndexKey(pageBefore, pageAfter)
}

/**
 * Calculate a new index key for creating a new frame at a specified position (toIndex).
 *
 * Example:
 * - If inserting a new frame at index 2, generate an IndexKey that is between index 1 and 2.
 */
const calculateIndexKeyForNewFrame = (toIndex: number): IndexKey | null => {
  const editor = useTldrawEditor().editor
  const pages = editor?.getPages()
  if (pages == null) {
    return null
  }
  const pageBefore = pages[toIndex - 1]
  const pageAfter = pages[toIndex]
  return getNewIndexKey(pageBefore, pageAfter)
}

export const useWhiteboardFrameActions = (): {
  switchFrame: (frameId: TLPageId) => void
  addFrame: (destinationIndex: number) => void
  deleteFrame: (frameId: TLPageId) => Promise<void>
  moveFrame: (frameId: TLPageId, fromIndex: number, toIndex: number) => void
} => {
  const whiteboardModeStore = useWhiteboardModeStore()
  const whiteboardFrameBackgroundStore = useWhiteboardFrameBackgroundStore()

  const switchFrame = (frameId: TLPageId): void => {
    const editor = useTldrawEditor().editor
    if (editor == null) return
    editor.setCurrentPage(frameId)
    setSearchParam(FRAME_ID_QUERY_PARAM, frameId)

    if (whiteboardModeStore.isPlayMode) {
      prefetchImagesInAdjacentFrames(editor)
    }
  }

  const addFrame = (destinationIndex: number): void => {
    const editor = useTldrawEditor().editor
    if (editor == null) return
    const newPageIndexKey = calculateIndexKeyForNewFrame(destinationIndex)
    if (newPageIndexKey == null) {
      return
    }
    const newPageId = PageRecordType.createId()
    const pagePayload: Partial<TLPage> = {
      name: 'Page',
      id: newPageId,
      index: newPageIndexKey,
      meta: {
        backgroundType: whiteboardFrameBackgroundStore.defaultFrameBackground.backgroundType,
        customBackgroundUrl: whiteboardFrameBackgroundStore.defaultFrameBackground.backgroundUrl,
      },
    }

    editor.createPage(pagePayload as TLPage)
    editor.markHistoryStoppingPoint('create page')
    switchFrame(newPageId)
  }

  const deleteFrame = async (frameId: TLPageId): Promise<void> => {
    const editor = useTldrawEditor().editor
    const whiteboardStore = useWhiteboardStore()
    if (editor == null) return
    const shapeIdsForPage = editor.getPageShapeIds(frameId)

    editor.markHistoryStoppingPoint('delete page and shapes')
    // We need to batch because deletePage does not automatically delete shapes
    // so they are orphaned in the store and it causes inconsistent undo behavior
    // (probably due to garbage collection)
    await editor.run(
      () => {
        editor.deleteShapes([...shapeIdsForPage])
        editor.deletePage(frameId)
      },
      { history: 'record' },
    )

    const currentFrameId = editor.getCurrentPageId()
    const frameIndex = whiteboardStore.getFrameIndex(currentFrameId)

    // When deleting a frame, focus on the button representing the current frame in WhiteboardFramePreview
    const frameElement = document.querySelector(
      `[data-testid="whiteboardFrame${frameIndex + 1}PreviewButton"]`,
    ) as HTMLButtonElement

    if (frameElement != null) {
      frameElement.focus()
    }
  }

  const moveFrame = (frameId: TLPageId, fromIndex: number, toIndex: number): void => {
    const toIndexKey = calculateIndexKeyForMoveFrame(fromIndex, toIndex)
    if (toIndexKey == null) {
      throw new Error('toIndexKey not found')
    }
    const editor = useTldrawEditor().editor
    if (editor == null) return
    editor.markHistoryStoppingPoint('moving page')
    const selectedFrame = editor.getPage(frameId)
    if (selectedFrame != null) {
      editor.updatePage({
        ...selectedFrame,
        index: toIndexKey,
      })
    }
  }

  return {
    switchFrame,
    addFrame,
    deleteFrame,
    moveFrame,
  }
}
